import { forwardRef, useState, useRef, useEffect } from 'react';
import { clsx } from 'clsx';
import { useDebounce } from 'hooks/useDebounce';
import { AutoCompleteItem } from './AutoCompleteTypes';

interface AutoCompleteProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'value'> {
  label?: string;
  error?: string;
  required?: boolean;
  items: AutoCompleteItem[];
  value?: AutoCompleteItem | null;
  onChange: (item: AutoCompleteItem | null) => void;
  onSearch: (query: string) => void;
  isLoading?: boolean;
  placeholder?: string;
  noResultsText?: string;
  minChars?: number;
}

export const AutoComplete = forwardRef<HTMLInputElement, AutoCompleteProps>(
  (
    {
      label,
      error,
      required,
      items,
      value,
      onChange,
      onSearch,
      isLoading,
      placeholder,
      noResultsText = 'Aucun résultat',
      minChars = 2,
      className,
      disabled,
      ...props
    },
    ref
  ) => {
    const [isOpen, setIsOpen] = useState(false);
    const [inputValue, setInputValue] = useState(value?.label || '');
    const [highlightedIndex, setHighlightedIndex] = useState(0);
    const containerRef = useRef<HTMLDivElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);

    const debouncedSearch = useDebounce((query: string) => {
      if (query.length >= minChars) {
        onSearch(query);
      }
    }, 300);

    useEffect(() => {
      setInputValue(value?.label || '');
    }, [value]);

    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
          setIsOpen(false);
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      setInputValue(newValue);
      setIsOpen(true);
      setHighlightedIndex(0);

      if (!newValue) {
        onChange(null);
      }

      debouncedSearch(newValue);
    };

    const handleSelectItem = (item: AutoCompleteItem) => {
      setInputValue(item.label);
      onChange(item);
      setIsOpen(false);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (!isOpen) {
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
          setIsOpen(true);
        }
        return;
      }

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setHighlightedIndex((prev) => (prev < items.length - 1 ? prev + 1 : prev));
          break;
        case 'ArrowUp':
          e.preventDefault();
          setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : prev));
          break;
        case 'Enter':
          e.preventDefault();
          if (items[highlightedIndex]) {
            handleSelectItem(items[highlightedIndex]);
          }
          break;
        case 'Escape':
          setIsOpen(false);
          break;
      }
    };

    const handleInputClick = () => {
      if (!inputValue || inputValue.length === 0) {
        // Déclencher une recherche avec une chaîne vide
        onSearch('');
        setIsOpen(true);
      } else if (inputValue.length >= minChars) {
        setIsOpen(true);
      }
    };

    const showDropdown = isOpen && !disabled && (inputValue.length >= minChars || inputValue.length === 0);
    const showNoResults = showDropdown && !isLoading && items.length === 0;

    return (
      <div className="relative" ref={containerRef}>
        {label && (
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {label}
            {required && <span className="text-red-500 ml-1">*</span>}
          </label>
        )}

        <div className="relative">
          <input
            ref={ref}
            type="text"
            onClick={handleInputClick}
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onFocus={() => inputValue.length >= minChars && setIsOpen(true)}
            placeholder={placeholder}
            disabled={disabled}
            className={clsx(
              "w-full px-4 py-4 rounded border text-sm font-normal text-gray-700 box-border",
              "placeholder:text-gray-500 placeholder:italic",
              "focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary",
              "h-12",
              error ? "border-red-500" : "border-gray-300",
              disabled && "bg-gray-100 cursor-not-allowed",
              className
            )}
            {...props}
          />

          {isLoading && (
            <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
            </div>
          )}
        </div>

        {showDropdown && (
          <div
            ref={dropdownRef}
            className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto"
          >
            {isLoading ? (
              <div className="px-4 py-3 text-sm text-gray-500 text-center">
                Chargement...
              </div>
            ) : showNoResults ? (
              <div className="px-4 py-3 text-sm text-gray-500 text-center">
                {noResultsText}
              </div>
            ) : (
              items.map((item, index) => (
                <div
                  key={item.id}
                  className={clsx(
                    "px-4 py-3 text-sm cursor-pointer transition-colors",
                    highlightedIndex === index
                      ? "bg-primary/10 text-primary"
                      : "text-gray-700 hover:bg-gray-100"
                  )}
                  onClick={() => handleSelectItem(item)}
                  onMouseEnter={() => setHighlightedIndex(index)}
                >
                  {item.label}
                </div>
              ))
            )}
          </div>
        )}

        {error && (
          <p className="mt-1 text-sm text-red-500">{error}</p>
        )}
      </div>
    );
  }
);

AutoComplete.displayName = 'AutoComplete';
